import { Box, type BoxT } from '@mentimeter/ragnar-ui/box';
import {
  Children,
  cloneElement,
  isValidElement,
  forwardRef,
  type ReactElement,
} from 'react';
import { getDotRadius } from './getDotRadius';

const VERTICAL_GRID_GAP_PERCENTAGE = 0.01;
const HORIZONTAL_GRID_GAP_PERCENTAGE = 0.025;

const LAYOUT_MAP: Record<number, number[]> = {
  1: [2],
  2: [2],
  3: [3],
  4: [2, 2],
  5: [3, 2],
  6: [3, 3],
  7: [4, 3],
  8: [4, 4],
  9: [3, 3, 3],
  10: [3, 4, 3],
  11: [4, 3, 4],
  12: [4, 4, 4],
};

interface ChildrenProps {
  width: number;
  height: number;
  dotRadius: number;
}

interface DotsGridProps extends BoxT {
  width: number;
  height: number;
  largestNumberOfDots?: number;
  children: ReactElement<ChildrenProps> | Array<ReactElement<ChildrenProps>>;
}

const getElementSize = ({
  totalSize,
  numberOfElements,
  gap,
}: {
  totalSize: number;
  numberOfElements: number;
  gap: number;
}) => {
  const sizeWithoutGaps = totalSize - gap * (numberOfElements - 1);
  return sizeWithoutGaps / numberOfElements;
};

export const DotsGrid = forwardRef<HTMLDivElement, DotsGridProps>(
  ({ width, height, largestNumberOfDots = 1, children, ...rest }, ref) => {
    const childrenArray = Children.toArray(children);
    const count = childrenArray.length;

    if (count > 12 || count < 1) {
      return null;
    }

    const layout = LAYOUT_MAP[count]!;

    const horizontalGap =
      width > 1000
        ? width * HORIZONTAL_GRID_GAP_PERCENTAGE
        : (width * HORIZONTAL_GRID_GAP_PERCENTAGE) / 2;

    const verticalGap = height * VERTICAL_GRID_GAP_PERCENTAGE;

    const childWidth = getElementSize({
      totalSize: width,
      numberOfElements: Math.max(...layout),
      gap: horizontalGap,
    });
    const childHeight = getElementSize({
      totalSize: height,
      numberOfElements: layout.length,
      gap: verticalGap,
    });

    const dotRadius = getDotRadius({
      numberOfDots: largestNumberOfDots,
      width: childWidth,
      height: childHeight,
    });

    return (
      <Box
        ref={ref}
        display="flex"
        flexDirection="column"
        alignItems="center"
        width={width}
        height={height}
        gap={verticalGap}
        {...rest}
      >
        {layout.map((cols, rowIndex) => (
          <Box
            key={rowIndex}
            gap={horizontalGap}
            flexDirection="row"
            justifyItems="center"
            alignItems="center"
          >
            {childrenArray.splice(0, cols).map((child, index) =>
              isValidElement<ChildrenProps>(child)
                ? cloneElement(child, {
                    width: childWidth,
                    height: childHeight,
                    dotRadius,
                    key: index,
                  })
                : child,
            )}
          </Box>
        ))}
      </Box>
    );
  },
);
